package er.prateek.crossly.notifications

import er.prateek.crossly.config.NotificationsConfig
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

private const val MINUTES_PER_DAY = 24 * 60

/**
 * Pure decision logic for "when should today's reminder fire," kept separate from
 * [NotificationCoordinator] so it's trivially unit-testable with no fakes.
 */
object OpenTimePersonalizer {

    /**
     * The player's typical open time from [history] (minute-of-day samples, most recent last),
     * offset by [NotificationsConfig.reminderOffsetMinutes] and pushed out of quiet hours.
     * Falls back to [NotificationsConfig.fallbackReminderHourLocal] until enough samples exist.
     *
     * Averages with a circular mean rather than a plain arithmetic one -- a player who opens the
     * app at 23:50 and 00:10 opens "at midnight," not at the arithmetic mean of noon.
     */
    fun reminderHourMinute(history: List<Int>, config: NotificationsConfig): Pair<Int, Int> {
        val usualMinute = if (history.size >= config.minOpenHistoryForPersonalization) {
            circularMeanMinute(history)
        } else {
            config.fallbackReminderHourLocal * 60
        }
        val target = (usualMinute + config.reminderOffsetMinutes).mod(MINUTES_PER_DAY)
        val clamped = clampOutsideQuietHours(target, config)
        return clamped / 60 to clamped % 60
    }

    private fun circularMeanMinute(history: List<Int>): Int {
        var sumSin = 0.0
        var sumCos = 0.0
        for (minute in history) {
            val angle = 2 * kotlin.math.PI * minute / MINUTES_PER_DAY
            sumSin += sin(angle)
            sumCos += cos(angle)
        }
        val meanAngle = atan2(sumSin / history.size, sumCos / history.size)
        val normalizedAngle = if (meanAngle < 0) meanAngle + 2 * kotlin.math.PI else meanAngle
        return ((normalizedAngle / (2 * kotlin.math.PI)) * MINUTES_PER_DAY).roundToInt().mod(MINUTES_PER_DAY)
    }

    /** Quiet hours may wrap midnight (e.g. 22:00-08:00); a hit is pushed forward to the end. */
    private fun clampOutsideQuietHours(minuteOfDay: Int, config: NotificationsConfig): Int {
        val startMinute = config.quietHoursStart * 60
        val endMinute = config.quietHoursEnd * 60
        val inQuietHours = if (startMinute > endMinute) {
            minuteOfDay >= startMinute || minuteOfDay < endMinute
        } else {
            minuteOfDay in startMinute until endMinute
        }
        return if (inQuietHours) endMinute else minuteOfDay
    }
}
